import { createSignal, For, Show } from "solid-js";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TextArea } from "@/components/ui/textarea";
import { TextFieldRoot } from "@/components/ui/textfield";
import type { FreelancerFilterFormValues } from "@/domains/company/active-search/utils/filterParams";
import {
  categoryLabel,
  subcategoryLabel,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { cn } from "@/libs/cn";
import { useSmartFilters } from "../api";
import { useIsFixedOfferContext } from "../hooks/useIsFixedOfferContext";
import {
  isEmptySmartFiltersResponse,
  mapSmartFiltersToFormValues,
} from "../utils/smartFiltersMapping";

const MAX_LENGTH = 2000;
const MAX_VISIBLE_BADGES = 3;

type SmartFiltersProps = {
  onFiltersExtracted: (values: Partial<FreelancerFilterFormValues>) => void;
};

function collectExtractedLabels(
  values: Partial<FreelancerFilterFormValues>,
): string[] {
  const labels: string[] = [];
  if (values.search) labels.push(values.search);
  if (values.job_category) labels.push(categoryLabel(values.job_category));
  if (values.job_subcategory?.length)
    labels.push(...values.job_subcategory.map(subcategoryLabel));
  if (values.skills) labels.push(...values.skills);
  if (values.work_preference) labels.push(values.work_preference);
  if (values.experience_level) labels.push(values.experience_level);
  return labels;
}

export function SmartFilters(props: SmartFiltersProps) {
  const { t } = useI18n();
  const [text, setText] = createSignal<string>("");
  const [extractedValues, setExtractedValues] =
    createSignal<Partial<FreelancerFilterFormValues> | null>(null);
  const extractedLabels = () => {
    const values = extractedValues();
    return values ? collectExtractedLabels(values) : [];
  };
  const mutation = useSmartFilters();
  const isFixedOffer = useIsFixedOfferContext();

  const visibleBadges = () => extractedLabels().slice(0, MAX_VISIBLE_BADGES);
  const hiddenCount = () =>
    Math.max(0, extractedLabels().length - MAX_VISIBLE_BADGES);

  const handleSubmit = () => {
    const value = text().trim();
    if (!value) return;

    mutation.mutate(value, {
      onSuccess: (response) => {
        if (isEmptySmartFiltersResponse(response)) {
          setExtractedValues(null);
          toast.info(
            t("company.search.freelancers.search.smart_filters.empty_result"),
          );
          return;
        }
        const mapped = mapSmartFiltersToFormValues(response, {
          allowPriceFields: !isFixedOffer(),
        });
        setExtractedValues(mapped);
        props.onFiltersExtracted(mapped);
        toast.success(
          t("company.search.freelancers.search.smart_filters.success"),
        );
      },
    });
  };

  return (
    <div
      class={cn(
        "relative rounded-xl border border-border bg-card p-6 transition-colors",
        mutation.isPending && "border-primary/40",
      )}
    >
      {/* Shimmer overlay during loading */}
      <Show when={mutation.isPending}>
        <div class="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-xl">
          <div class="absolute inset-0 animate-[shimmer_2s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-primary/5 to-transparent" />
        </div>
      </Show>

      <div class="mb-4 flex items-center gap-3">
        <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
          <i class="ph-fill ph-sparkle text-lg text-primary" />
        </div>
        <div class="flex items-center gap-2">
          <h3 class="font-display text-lg font-semibold text-foreground">
            {t("company.search.freelancers.search.smart_filters.title")}
          </h3>
          <Badge variant="light-primary" size="sm">
            AI
          </Badge>
        </div>
      </div>

      <p class="mb-4 text-sm leading-relaxed text-muted-foreground">
        {t("company.search.freelancers.search.smart_filters.description")}
      </p>

      <TextFieldRoot>
        <TextArea
          value={text()}
          onInput={(e) => setText(e.currentTarget.value)}
          placeholder={t(
            "company.search.freelancers.search.smart_filters.placeholder",
          )}
          maxLength={MAX_LENGTH}
          class="min-h-[100px] resize-none border-border bg-background text-sm transition-colors focus:border-primary/40"
        />
      </TextFieldRoot>

      <div class="mt-3 flex items-center justify-between gap-3">
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <Show when={extractedLabels().length > 0}>
            <i class="ph-fill ph-check-circle text-base text-green-600 shrink-0" />
            <div class="flex min-w-0 flex-wrap items-center gap-1.5">
              <For each={visibleBadges()}>
                {(label) => (
                  <Badge variant="outline" size="sm">
                    {label}
                  </Badge>
                )}
              </For>
              <Show when={hiddenCount() > 0}>
                <span class="text-xs text-muted-foreground shrink-0">
                  +{hiddenCount()}{" "}
                  {String(
                    t("company.search.freelancers.search.smart_filters.others"),
                  )}
                </span>
              </Show>
            </div>
          </Show>
          <Show when={extractedLabels().length === 0 && text().length > 0}>
            <span class="text-xs text-muted-foreground">
              {text().length}/{MAX_LENGTH}
            </span>
          </Show>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!text().trim() || mutation.isPending}
          onClick={handleSubmit}
          class="shrink-0"
        >
          <Show
            when={!mutation.isPending}
            fallback={
              <span class="flex items-center gap-2">
                <span class="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                {t("company.search.freelancers.search.smart_filters.loading")}
              </span>
            }
          >
            <i class="ph-fill ph-sparkle text-sm" />
            {t("company.search.freelancers.search.smart_filters.submit")}
          </Show>
        </Button>
      </div>
    </div>
  );
}
